import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import "./DailyInventory.css";
import { getDailyInventoryReport } from "../../api/reportsAPI";
import Inputs from "../../components/inputs/Inputs";

const INITIAL_FROM_DATE = moment().subtract(1, "days").format("YYYY-MM-DD");
const INITIAL_TO_DATE = moment().subtract(1, "days").format("YYYY-MM-DD");

const DailyInventory = () => {
    const [searchText, setSearchText] = useState("");
    const [inventoryData, setInventoryData] = useState([]);
    // const [filteredStockData, setilteredStockData] = useState([]);
    
    const [formValues, setFormValues] = useState({
        fromDate: INITIAL_FROM_DATE,
        toDate: INITIAL_TO_DATE,
    });
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);


    // Load report once when component mounts.
    useEffect(() => {
        loadInventoryReport({
            startDate: INITIAL_FROM_DATE,
            endDate: INITIAL_TO_DATE,
        });
    }, []);

    


    const loadInventoryReport = async () => {

        setIsLoading(true);
        try {
            const response = await getDailyInventoryReport({
                startDate: formValues.fromDate,
                endDate: formValues.toDate,
            });

            if (response?.status !== 200) {
                throw new Error(
                    response?.data?.message ||
                    "Could not load the daily sales report"
                );
            }

            setInventoryData(response?.data?.data ?? response?.data ?? [])

        } catch (error) {

        } finally {
            setIsLoading(false);
        }

    }


    const stockData = useMemo(() => {
        const rawData = inventoryData ?? [];
        if (!Array.isArray(rawData)) {
            return [];
        }

        return rawData.flat().filter(Boolean);
    }, [inventoryData]);

    const safeNumber = (value) => {
        const number = Number(value);
        return Number.isFinite(number) ? number : 0;
    };

    const formatNumber = (value) => {
        return safeNumber(value).toLocaleString("en-US", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        });
    };

    const formatDate = (dateValue) => {
        if (!dateValue) return "-";

        const parsedDate = moment(dateValue);

        return parsedDate.isValid()
            ? parsedDate.format("DD MMM YYYY")
            : "-";
    };

    const filteredStockData = useMemo(() => {
        const keyword = searchText.trim().toLowerCase();

        if (!keyword) {
            return stockData;
        }

        return stockData.filter((item) => {
            const itemCode = String(item?.item_code ?? "").toLowerCase();
            const itemDescription = String(
                item?.item_description ?? ""
            ).toLowerCase();

            return (
                itemCode.includes(keyword) ||
                itemDescription.includes(keyword)
            );
        });
    }, [stockData, searchText]);

    const totals = useMemo(() => {
        return filteredStockData.reduce(
            (total, item) => ({
                openBalance: total.openBalance + safeNumber(item?.open_bal),
                stockIn: total.stockIn + safeNumber(item?.stock_in),
                stockOut: total.stockOut + safeNumber(item?.stock_out),
                closeBalance: total.closeBalance + safeNumber(item?.close_bal),
            }),
            {
                openBalance: 0,
                stockIn: 0,
                stockOut: 0,
                closeBalance: 0,
            }
        );
    }, [filteredStockData]);

    //   if (response?.success === false) {
    //     return (
    //       <div className="daily-stock-report__error">
    //         Could not load the stock report.
    //       </div>
    //     );
    //   }


    const normalizeDateValue = (value) => {
        // Supports custom Input components that return:
        // 1. "2026-09-23"
        // 2. Date object
        // 3. an event object
        const rawValue = value?.target?.value ?? value;

        if (!rawValue) {
            return "";
        }

        const parsedDate = moment(rawValue);

        return parsedDate.isValid()
            ? parsedDate.format("YYYY-MM-DD")
            : "";
    };

    const handleChange = (name, value) => {
        const normalizedValue = normalizeDateValue(value);

        setFormValues((previous) => ({
            ...previous,
            [name]: normalizedValue,
        }));

        setErrors((previous) => ({
            ...previous,
            [name]: undefined,
        }));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        loadInventoryReport(formValues);
    };

    return (
        <section className="daily-stock-report">
            <form className="sales-report__filters" onSubmit={handleSubmit}>
                <div className="sales-report__filter-row">
                    <div className="sales-report__filter-field">
                        <Inputs
                            name="fromDate"
                            size="small"
                            label="Start Date"
                            value={formValues.fromDate}
                            inputType="date"
                            onChange={(value) => handleChange("fromDate", value)}
                            error={Boolean(errors.fromDate)}
                            helperText={errors.fromDate?.[0] ?? ""}
                        />
                    </div>

                    <div className="sales-report__filter-field">
                        <Inputs
                            name="toDate"
                            size="small"
                            label="End Date"
                            value={formValues.toDate}
                            inputType="date"
                            maxDate={new Date()}
                            onChange={(value) => handleChange("toDate", value)}
                            error={Boolean(errors.toDate)}
                            helperText={errors.toDate?.[0] ?? ""}
                        />
                    </div>

                    <div className="sales-report__filter-action">
                        <button
                            type="submit"
                            className="sales-report__fetch-button"
                            disabled={isLoading}
                        >
                            {isLoading ? "Loading..." : "Fetch Data"}
                        </button>
                    </div>
                </div>
            </form>
            <header className="daily-stock-report__header">
                <div>
                    <p className="daily-stock-report__eyebrow">Inventory overview</p>
                    <h2 className="daily-stock-report__title">Daily Stock Report</h2>
                    <p className="daily-stock-report__subtitle">
                        Review opening balance, stock movement, and closing stock balance.
                    </p>
                </div>

                <div className="daily-stock-report__count">
                    <span>Items</span>
                    <strong>{formatNumber(filteredStockData.length)}</strong>
                </div>
            </header>
            {
                formValues.fromDate === formValues.toDate && 
                <div className="daily-stock-report__summary-grid">
                <article className="stock-summary-card stock-summary-card--blue">
                    <span>Opening balance</span>
                    <strong>{formatNumber(totals.openBalance)}</strong>
                </article>

                <article className="stock-summary-card stock-summary-card--green">
                    <span>Stock in</span>
                    <strong>{formatNumber(totals.stockIn)}</strong>
                </article>

                <article className="stock-summary-card stock-summary-card--orange">
                    <span>Stock out</span>
                    <strong>{formatNumber(totals.stockOut)}</strong>
                </article>

                <article className="stock-summary-card stock-summary-card--purple">
                    <span>Closing balance</span>
                    <strong>{formatNumber(totals.closeBalance)}</strong>
                </article>
            </div>
            }
            

            <div className="daily-stock-report__toolbar">
                <div className="daily-stock-report__search">
                    <label htmlFor="stock-search">Search item</label>

                    <input
                        id="stock-search"
                        type="search"
                        placeholder="Search by item name or code..."
                        value={searchText}
                        onChange={(event) => setSearchText(event.target.value)}
                    />
                </div>
            </div>
            {
                filteredStockData.length > 0 && (
                    <div className="daily-stock-report__table-wrapper">
                        <table className="daily-stock-report__table">
                            <thead>
                                <tr>
                                    <th>Stock Date</th>
                                    <th>Item Code</th>
                                    <th>Item Description</th>
                                    <th className="text-right">Opening</th>
                                    <th className="text-right">Stock In</th>
                                    <th className="text-right">Stock Out</th>
                                    <th className="text-right">Closing</th>
                                    <th>Status</th>
                                </tr>
                            </thead>

                            <tbody>
                                {filteredStockData.length === 0 ? (
                                    <tr>
                                        <td
                                            colSpan="8"
                                            className="daily-stock-report__empty"
                                        >
                                            No stock records found.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredStockData.map((item) => (
                                        <tr key={item.id}>
                                            <td>{formatDate(item.eod_date)}</td>
                                            <td>
                                                <span className="item-code">
                                                    {item.item_code || "-"}
                                                </span>
                                            </td>

                                            <td>{item.item_description || "-"}</td>

                                            <td className="text-right">
                                                {formatNumber(item.open_bal)}
                                            </td>

                                            <td className="text-right stock-in">
                                                +{formatNumber(item.stock_in)}
                                            </td>

                                            <td className="text-right stock-out">
                                                -{formatNumber(item.stock_out)}
                                            </td>

                                            <td className="text-right closing-balance">
                                                {formatNumber(item.close_bal)}
                                            </td>

                                            <td>
                                                <span
                                                    className={`status-badge ${item.status === "A"
                                                            ? "status-badge--active"
                                                            : "status-badge--inactive"
                                                        }`}
                                                >
                                                    {item.status === "A" ? "Active" : "Inactive"}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>

                            {filteredStockData.length > 0 && (
                                <tfoot>
                                    <tr>
                                        <td colSpan="3">Totals</td>
                                        <td className="text-right">
                                            {formatNumber(totals.openBalance)}
                                        </td>
                                        <td className="text-right stock-in">
                                            +{formatNumber(totals.stockIn)}
                                        </td>
                                        <td className="text-right stock-out">
                                            -{formatNumber(totals.stockOut)}
                                        </td>
                                        <td className="text-right closing-balance">
                                            {formatNumber(totals.closeBalance)}
                                        </td>
                                        <td>-</td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                )
            }

        </section>
    );
};

export default DailyInventory;